// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  DatePicker,
  Icon,
  IconName,
  MultiSelect,
  Tooltip,
} from '@usealloy/component-library';
import type {
  MultiSelectProps,
  Option,
} from '@usealloy/component-library';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { components as selectComponents } from 'react-select';
import type { MenuListProps } from 'react-select';
import AggregationSelect from './AggregationSelect';
import AggregationSearchableDropdown from './AggregationSearchableDropdown';
import { BOOLEAN_FILTER_OPTIONS, REFERENCE_FIELD_PLACEHOLDER } from './constants';
import type { AggregationFilter } from 'flow/store/engine/types';
import type { AggregationFilterOperatorType, AggregationFilterOption } from './types';
import { AggregationFilterOperator } from './types';
import {
  buildOptionGroups,
  getColumnKey,
  getOperatorOptionsForFilter,
  getReferenceableFieldOptions,
  resolveDataType,
} from './helpers';
import {
  StyledFilterCriteriaRow,
  StyledAggregationClause,
  StyledFilterValueInput,
  StyledFilterDatePickerWrapper,
  StyledAggregationSelectWrapper,
  StyledDynamicValueLabel,
  StyledFieldReferenceWrapper,
  StyledPiiOperatorFooter,
} from './styles';
import AggregationNumberInput from './AggregationNumberInput';
import { RuleBuilderContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import { AggregationEditorContext } from './context';
import { useAppSelector } from '~/store/reduxHooks';
import { selectWorkflowType } from 'flow/store/redux/selectors';

type AggregationFilterRowProps = {
  filter: AggregationFilter;
  filterOption?: AggregationFilterOption;
  onUpdate: (filterKey: string, updates: Partial<AggregationFilter>) => void;
  onRemove: (filterKey: string) => void;
};

const defaultStaticValueForOperator = (
  operator: AggregationFilterOperatorType,
): AggregationFilter['staticValue'] => {
  switch (operator) {
    case AggregationFilterOperator.BT: return ['', ''];
    case AggregationFilterOperator.IN: return [];
    default: return '';
  }
};

// -- Sub-components ---------------------------------------------------------

/** Multi-select dropdown for the IN operator when possible_values is defined. */
const AggregationMultiSelect = ({
  id,
  options,
  values,
  onChange,
}: {
  id: string;
  options: Option[];
  values: string[];
  onChange: (values: string[]) => void;
}) => {
  const { multiSelectProps } = useContext(AggregationEditorContext);

  const selectedOptions: Option[] = values.map(selectedValue => {
    const match = options.find(option => option.value === selectedValue);
    return match ?? { value: selectedValue, label: selectedValue };
  });

  const mergedStyleFunctions = useMemo<MultiSelectProps['customStyleFunctions']>(() => ({
    ...multiSelectProps.customStyleFunctions,
    placeholder: () => ({
      color: 'var(--cl-text-code)',
      fontWeight: 'var(--cl-font-weight-bold)',
    }),
  }), [multiSelectProps.customStyleFunctions]);

  return (
    <StyledAggregationSelectWrapper>
      <MultiSelect
        {...multiSelectProps}
        id={id}
        customStyleFunctions={mergedStyleFunctions}
        options={options}
        selectedOptions={selectedOptions}
        onChange={(raw: readonly Option[]) => {
          const selected = raw
            .map(option => option.value)
            .filter((value): value is string => value !== null);
          onChange(selected);
        }}
        isNaked
        isDisabled={false}
        isMulti
        placeholder="[select values]"
      />
    </StyledAggregationSelectWrapper>
  );
};

/** Single value input — renders the appropriate control based on the filter option's data type. */
const FilterDataTypeInput = ({
  id,
  value,
  filterOption,
  possibleValues,
  onChange,
}: {
  id: string;
  value: string;
  filterOption?: AggregationFilterOption;
  possibleValues: string[];
  onChange: (val: string) => void;
}) => {
  const { multiSelectProps } = useContext(AggregationEditorContext);

  if (possibleValues.length) {
    return (
      <AggregationSelect
        id={id}
        multiSelectProps={multiSelectProps}
        options={possibleValues.map(possibleValue => ({
          value: possibleValue,
          label: possibleValue,
        }))}
        value={value}
        placeholder="[select value]"
        onChange={(option: Option) => onChange(option.value)}
      />
    );
  }

  const uiDataType = resolveDataType(filterOption);

  switch (uiDataType) {
    case 'boolean':
      return (
        <AggregationSelect
          id={id}
          multiSelectProps={multiSelectProps}
          options={BOOLEAN_FILTER_OPTIONS}
          value={value}
          placeholder="[select value]"
          onChange={(option: Option) => onChange(option.value)}
        />
      );

    case 'integer':
    case 'decimal':
      return (
        <AggregationNumberInput
          minValue={0}
          maxValue={Number.MAX_SAFE_INTEGER}
          decimalScale={uiDataType === 'decimal' ? 2 : undefined}
          value={Number(value)}
          onHandleValueChange={(val: number) => onChange(String(val))}
        />
      );
    case 'date':
      return (
        <StyledFilterDatePickerWrapper>
          <DatePicker
            value={value ? dayjs(value) : null}
            onChange={(date: Dayjs) => onChange(date.format('YYYY-MM-DD'))}
            style={{ width: '100%' }}
          />
        </StyledFilterDatePickerWrapper>
      );
    case 'timestamp':
      return (
        <StyledFilterDatePickerWrapper>
          <DatePicker
            showTime
            value={value ? dayjs(value) : null}
            onChange={(date: Dayjs) => onChange(dayjs.utc(date).format('YYYY-MM-DDTHH:mm:ss[Z]'))}
            style={{ width: '100%' }}
          />
        </StyledFilterDatePickerWrapper>
      );
    default:
      return (
        <StyledFilterValueInput
          type="text"
          value={value}
          placeholder="[enter value]"
          onChange={e => onChange(e.target.value)}
        />
      );
  }
};

/** Renders value input(s) based on the operator type. */
const FilterValueInput = ({
  filterKey,
  filter,
  filterOption,
  onUpdate,
}: {
  filterKey: string;
  filter: AggregationFilter;
  filterOption?: AggregationFilterOption;
  onUpdate: AggregationFilterRowProps['onUpdate'];
}) => {
  const { containerType } = useContext(RuleBuilderContext);
  const staticValue = filter.staticValue
    ?? defaultStaticValueForOperator(filter.operatorSlug);
  const possibleValues = filterOption?.possible_values ?? [];

  switch (filter.operatorSlug) {
    case AggregationFilterOperator.BT: {
      const [lower = '', upper = ''] = Array.isArray(staticValue)
        ? staticValue
        : ['', ''];
      return (
        <>
          <FilterDataTypeInput
            id={`agg-filter-${filterKey}-lower`}
            value={lower}
            filterOption={filterOption}
            possibleValues={possibleValues}
            onChange={val => onUpdate(filterKey, { staticValue: [val, upper] })}
          />
          <StyledAggregationClause containerType={containerType}>
            and
          </StyledAggregationClause>
          <FilterDataTypeInput
            id={`agg-filter-${filterKey}-upper`}
            value={upper}
            filterOption={filterOption}
            possibleValues={possibleValues}
            onChange={val => onUpdate(filterKey, { staticValue: [lower, val] })}
          />
        </>
      );
    }

    case AggregationFilterOperator.IN: {
      const values = Array.isArray(staticValue) ? staticValue : [];

      if (possibleValues.length) {
        return (
          <AggregationMultiSelect
            id={`agg-filter-${filterKey}-in`}
            options={possibleValues.map(possibleValue => ({
              value: possibleValue,
              label: possibleValue,
            }))}
            values={values}
            onChange={updated => onUpdate(filterKey, { staticValue: updated })}
          />
        );
      }

      return (
        <StyledFilterValueInput
          type="text"
          value={values.join(', ')}
          placeholder="[comma-separated values]"
          onChange={e => {
            const updated = e.target.value.split(',').map(rawEntry => rawEntry.trim()).filter(Boolean);
            onUpdate(filterKey, { staticValue: updated });
          }}
        />
      );
    }
    default:
      return (
        <FilterDataTypeInput
          id={`agg-filter-${filterKey}-value`}
          value={typeof staticValue === 'string' ? staticValue : ''}
          filterOption={filterOption}
          possibleValues={possibleValues}
          onChange={val => onUpdate(filterKey, { staticValue: val })}
        />
      );
  }
};

/**
 * Custom MenuList for PII Published Attribute filters.
 *
 * PII values are hashed at rest, so only equality-based operator
 * are valid — the backend already restricts the operator set via the config.
 * This component appends an informational footer to the operator dropdown so
 * users understand *why* only those operators are available.
 */
const PiiOperatorMenuList = (props: MenuListProps) => (
  <>
    <selectComponents.MenuList {...props} />
    <StyledPiiOperatorFooter>
      <Icon icon={IconName.Info} size="small" />
      <span>
        Published Attributes containing PII
        only support exact string matching
      </span>
    </StyledPiiOperatorFooter>
  </>
);

/**
 * Picker for the column a dynamic filter reads its value from, defaulting to the
 * filtered column itself. Opens the same menu the Add/Remove Filter control uses.
 *
 * With one option or none there is nothing to choose between, so the selection
 * renders as a plain label rather than an affordance that leads nowhere — the
 * case for published attributes and device details, neither of which can be
 * named as a source.
 */
const FieldReferencePicker = ({
  options,
  selectedSlug,
  fallbackLabel,
  objectDisplayName,
  onSelect,
}: {
  options: AggregationFilterOption[];
  selectedSlug: string;
  fallbackLabel: string;
  objectDisplayName: string;
  onSelect: (columnSlug: string) => void;
}) => {
  const groups = useMemo(
    () => buildOptionGroups({ options, objectDisplayName }),
    [options, objectDisplayName],
  );

  if (options.length <= 1) {
    const selected = options.find(option => option.value === selectedSlug);
    return (
      <StyledDynamicValueLabel>
        {`Current ${selected?.label ?? fallbackLabel}`}
      </StyledDynamicValueLabel>
    );
  }

  return (
    <StyledFieldReferenceWrapper data-filter-reference>
      <AggregationSearchableDropdown
        mode="single"
        groups={groups}
        value={selectedSlug}
        triggerPrefix="Current"
        placeholder={REFERENCE_FIELD_PLACEHOLDER}
        searchPlaceholder="Search"
        emptyMessage="No fields available."
        onSelect={option => onSelect(option.value)}
      />
    </StyledFieldReferenceWrapper>
  );
};

// -- Main component ---------------------------------------------------------

const AggregationFilterRow = ({
  filter,
  filterOption,
  onUpdate,
  onRemove,
}: AggregationFilterRowProps) => {
  const { containerType } = useContext(RuleBuilderContext);
  const { multiSelectProps, aggregationConfig } = useContext(AggregationEditorContext);
  const isTransactionWorkflow = useAppSelector(selectWorkflowType) === 'transaction';

  const filterKey = getColumnKey(filter);
  const label = filterOption?.label ?? filter.columnSlug;
  const isDynamicColumnSlugSet = !!filter.dynamicColumnSlug;
  const isTransactionObject = aggregationConfig.objectType === 'transaction';
  const dynamicValueLabel = isTransactionObject
    ? 'supplied payload value'
    : `${aggregationConfig.objectDisplayName}'s latest value`;
  // The backend config already restricts operator options for PII attributes
  // (OAK-1060). The frontend only needs to surface the explanatory footer in
  // the operator dropdown — no client-side filtering of operators is needed.
  const isPii = !!filterOption?.contains_pii;

  const piiComponents = { MenuList: PiiOperatorMenuList };

  const operatorOptions = getOperatorOptionsForFilter(filterOption, filter.operatorSlug);

  const referenceOptions = useMemo(
    () => getReferenceableFieldOptions({
      columns: aggregationConfig.columns,
      columnSlug: filter.columnSlug,
    }),
    [aggregationConfig.columns, filter.columnSlug],
  );

  const handleToggleDynamic = () => {
    if (!isDynamicColumnSlugSet) {
      onUpdate(filterKey, {
        dynamicColumnSlug: filter.columnSlug,
        staticValue: null,
      });
      return;
    }

    const dataType = resolveDataType(filterOption);
    const isNumeric = dataType === 'integer' || dataType === 'decimal';
    const fallbackStaticValue = isNumeric
      ? '0'
      : defaultStaticValueForOperator(filter.operatorSlug);
    onUpdate(filterKey, {
      dynamicColumnSlug: null,
      staticValue: fallbackStaticValue,
    });
  };

  return (
    <StyledFilterCriteriaRow>
      <StyledAggregationClause containerType={containerType} data-filter-label>
        {label}
      </StyledAggregationClause>
      <AggregationSelect
        id={`agg-filter-${filterKey}-operator`}
        multiSelectProps={multiSelectProps}
        options={operatorOptions}
        value={filter.operatorSlug}
        onChange={(option: Option) => {
          const nextOperator = option.value as AggregationFilterOperatorType;
          onUpdate(filterKey, {
            operatorSlug: nextOperator,
            staticValue: isDynamicColumnSlugSet
              ? null
              : defaultStaticValueForOperator(nextOperator),
          });
        }}
        {...(isPii && { components: piiComponents })}
      />
      {isDynamicColumnSlugSet ? (
        <FieldReferencePicker
          options={referenceOptions}
          selectedSlug={filter.dynamicColumnSlug}
          fallbackLabel={label}
          objectDisplayName={aggregationConfig.objectDisplayName}
          onSelect={columnSlug => onUpdate(filterKey, {
            dynamicColumnSlug: columnSlug,
            staticValue: null,
          })}
        />
      ) : (
        <FilterValueInput
          filterKey={filterKey}
          filter={filter}
          filterOption={filterOption}
          onUpdate={onUpdate}
        />
      )}
      <Tooltip
        content={isDynamicColumnSlugSet
          ? 'Switch to static value'
          : `Switch to ${dynamicValueLabel}`}
        side="bottom"
      >
        <Button
          size={ButtonSize.xsmall}
          variant={ButtonVariant.tertiary}
          onClick={handleToggleDynamic}
          disabled={isTransactionObject && !isTransactionWorkflow}
          aria-label={`Toggle dynamic value for ${label} filter`}
          data-filter-action
        >
          <Icon icon={IconName.Rerun} size="small" />
        </Button>
      </Tooltip>
      <Button
        size={ButtonSize.xsmall}
        variant={ButtonVariant.tertiary}
        onClick={() => onRemove(filterKey)}
        aria-label={`Remove ${label} filter`}
        data-filter-action
      >
        <Icon icon={IconName.Trash} size="small" />
      </Button>
    </StyledFilterCriteriaRow>
  );
};

export default AggregationFilterRow;
